import React, { useState, useEffect, useCallback, useRef } from 'react';
import { summaryService } from './SummaryModal.service';
import { formatDate, formatWordCount, getReadingTime, copyToClipboard, downloadAsFile } from './SummaryModal.utils';
import {
    SummaryModalProps,
    ELSummary,
    ELChatMessage,
    LoadingState,
    TabType,
    DEFAULT_CHAT_QUESTIONS
} from './SummaryModal.types';
import styles from './SummaryModal.module.css';
import FormattedMessageContent from './components/FormattedMessageContent/FormattedMessageContent';

const SummaryModal: React.FC<SummaryModalProps> = ({
    isOpen,
    onClose,
    elFileName,
    elId
}) => {
    const [activeTab, setActiveTab] = useState<TabType>(TabType.SUMMARY);
    const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.IDLE);
    const [summary, setSummary] = useState<ELSummary | null>(null);
    const [chatMessages, setChatMessages] = useState<ELChatMessage[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const generateSummary = useCallback(async () => {
        if (!elId) return;

        setLoadingState(LoadingState.LOADING);
        setError(null);
        setCurrentStep(0);

        // Simulate loading steps
        const stepInterval = setInterval(() => {
            setCurrentStep(prev => (prev < 2 ? prev + 1 : 0));
        }, 2000);

        const result = await summaryService.generateSummary({
            elId,
            elFileName,
            includeKeyPoints: true
        });

        clearInterval(stepInterval);

        if (result.success && result.data) {
            setSummary(result.data);
            setLoadingState(LoadingState.SUCCESS);
        } else {
            setError(result.error || 'Failed to generate summary');
            setLoadingState(LoadingState.ERROR);
        }
    }, [elId, elFileName]);

    const handleAskQuestion = async (question: string) => {
        if (!question.trim() || !elId) return;

        const userMessage: ELChatMessage = {
            id: Date.now().toString(),
            type: 'user',
            content: question.trim(),
            timestamp: new Date().toISOString()
        };

        setChatMessages(prev => [...prev, userMessage]);
        setChatLoading(true);
        setCurrentQuestion('');

        const result = await summaryService.generateSummary({
            elId,
            elFileName,
            includeKeyPoints: false,
            question: question.trim()
        });

        setChatLoading(false);

        if (result.success && result.data) {
            const assistantMessage: ELChatMessage = {
                id: result.data.id,
                type: 'assistant',
                content: result.data.content,
                timestamp: result.data.createdAt
            };
            setChatMessages(prev => [...prev, assistantMessage]);
        } else {
            const errorMessage: ELChatMessage = {
                id: (Date.now() + 1).toString(),
                type: 'assistant',
                content: 'Sorry, I encountered an error while processing your question. Please try again.',
                timestamp: new Date().toISOString()
            };
            setChatMessages(prev => [...prev, errorMessage]);
        }
    };

    const handleSuggestedQuestion = (question: string) => {
        handleAskQuestion(question);
    };

    const handleSubmitQuestion = (e: React.FormEvent) => {
        e.preventDefault();
        handleAskQuestion(currentQuestion);
    };

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages, chatLoading]);

    useEffect(() => {
        if (isOpen && elId && activeTab === TabType.SUMMARY && !summary) {
            generateSummary();
        }
    }, [isOpen, elId, activeTab, summary, generateSummary]);

    const handleClose = () => {
        setSummary(null);
        setChatMessages([]);
        setCurrentQuestion('');
        setError(null);
        setLoadingState(LoadingState.IDLE);
        setCopySuccess(false);
        setChatLoading(false);
        setActiveTab(TabType.SUMMARY);
        setCurrentStep(0);
        if (isSpeaking) {
            speechSynthesis.cancel();
            setIsSpeaking(false);
        }
        onClose();
    };

    const handleCopy = async () => {
        if (summary) {
            const success = await copyToClipboard(summary.content);
            if (success) {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            }
        }
    };

    const handleDownload = () => {
        if (summary) {
            const content = `${summary.content}\n\nKey Points:\n${summary.keyPoints.map(point => `• ${point}`).join('\n')}`;
            downloadAsFile(content, `summary.txt`);
        }
    };

    const handleRetry = () => {
        generateSummary();
    };

    const handleTextToSpeech = () => {
        if (!summary) return;

        if (isSpeaking) {
            speechSynthesis.cancel();
            setIsSpeaking(false);
            return;
        }

        const textToSpeak = `${summary.content}. Key Points: ${summary.keyPoints.join('. ')}`;
        const utterance = new SpeechSynthesisUtterance(textToSpeak);

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        speechSynthesis.speak(utterance);
    };

    if (!isOpen) return null;

    return (
        <div className="summaryModal">
            <div className={styles.modalOverlay} onClick={handleClose}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>EL Insights</h2>
                        <button className={styles.closeButton} onClick={handleClose} aria-label="Close modal">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    {/* Tab Container - Now Properly Visible */}
                    <div className={styles.tabContainer}>
                        <div className={styles.tabButtons}>
                            <button
                                className={`${styles.tabButton} ${activeTab === TabType.SUMMARY ? styles.activeTab : ''}`}
                                onClick={() => setActiveTab(TabType.SUMMARY)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={styles.tabIcon}>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Summary
                            </button>
                            <button
                                className={`${styles.tabButton} ${activeTab === TabType.CHAT ? styles.activeTab : ''}`}
                                onClick={() => setActiveTab(TabType.CHAT)}
                                disabled={loadingState !== LoadingState.SUCCESS}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={styles.tabIcon}>
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Chat
                            </button>
                        </div>
                    </div>

                    <div className={styles.content}>
                        {activeTab === TabType.SUMMARY && (
                            <>
                                {loadingState === LoadingState.LOADING && (
                                    <div className={styles.loadingContainer}>
                                        <div className={styles.modernIconContainer}>
                                            <div className={styles.modernAiIcon}>
                                                <svg viewBox="0 0 24 24" fill="none">
                                                    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor" opacity="0.8"/>
                                                    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none"/>
                                                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                                                </svg>
                                            </div>
                                        </div>

                                        <div className={styles.modernTextContainer}>
                                            <h3 className={styles.modernLoadingTitle}>
                                                Generating AI Summary
                                            </h3>
                                            <p className={styles.modernLoadingSubtitle}>
                                                Analyzing your engagement letter and extracting key insights...
                                            </p>
                                        </div>

                                        <div className={styles.modernProgressContainer}>
                                            <div className={styles.modernProgressBar}>
                                                <div className={styles.modernProgressFill}></div>
                                            </div>
                                        </div>

                                        <div className={styles.modernStepsContainer}>
                                            <div className={`${styles.modernStep} ${currentStep === 0 ? 'active' : ''}`}>
                                                <div className={styles.modernStepIcon}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                                                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                                                    </svg>
                                                </div>
                                                <span className={styles.modernStepText}>Reading Document</span>
                                            </div>
                                            <div className={`${styles.modernStep} ${currentStep === 1 ? 'active' : ''}`}>
                                                <div className={styles.modernStepIcon}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                                        <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" stroke="currentColor" strokeWidth="2"/>
                                                    </svg>
                                                </div>
                                                <span className={styles.modernStepText}>Analyzing Content</span>
                                            </div>
                                            <div className={`${styles.modernStep} ${currentStep === 2 ? 'active' : ''}`}>
                                                <div className={styles.modernStepIcon}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                                                    </svg>
                                                </div>
                                                <span className={styles.modernStepText}>Generating Insights</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {loadingState === LoadingState.ERROR && (
                                    <div className={styles.errorContainer}>
                                        <div className={styles.errorIcon}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                                <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2"/>
                                                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
                                            </svg>
                                        </div>
                                        <h3 className={styles.errorTitle}>Failed to generate summary</h3>
                                        <p className={styles.errorMessage}>{error}</p>
                                        <button className={styles.retryButton} onClick={handleRetry}>
                                            Try Again
                                        </button>
                                    </div>
                                )}

                                {loadingState === LoadingState.SUCCESS && summary && (
                                    <div className={styles.successContainer}>
                                        <div className={styles.headerInfo}>
                                            <div className={styles.meta}>
                                                <span className={styles.metaItem}>
                                                    {formatWordCount(summary.wordCount)}
                                                </span>
                                                <span className={styles.metaDivider}>•</span>
                                                <span className={styles.metaItem}>
                                                    {getReadingTime(summary.wordCount)}
                                                </span>
                                                <span className={styles.metaDivider}>•</span>
                                                <span className={styles.metaItem}>
                                                    Generated {formatDate(summary.createdAt)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={styles.contentSection}>
                                            <h4 className={styles.sectionTitle}>Summary</h4>
                                            <div className={styles.text}>
                                                {summary.content}
                                            </div>
                                        </div>

                                        <div className={styles.keypointsSection}>
                                            <h4 className={styles.sectionTitle}>Key Points</h4>
                                            <ul className={styles.keypointsList}>
                                                {summary.keyPoints.map((point, index) => (
                                                    <li key={index} className={styles.keypointItem}>
                                                        {point}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className={styles.actions}>
                                            <button
                                                className={`${styles.actionButton} ${styles.ttsButton} ${isSpeaking ? styles.speaking : ''}`}
                                                onClick={handleTextToSpeech}
                                                aria-label={isSpeaking ? 'Stop reading' : 'Read aloud'}
                                            >
                                                {isSpeaking ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                        <rect x="6" y="4" width="4" height="16" rx="2" fill="currentColor"/>
                                                        <rect x="14" y="4" width="4" height="16" rx="2" fill="currentColor"/>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                        <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"
                                                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                                <span className={styles.ttsText}>
                                                    {isSpeaking ? 'Stop' : 'Listen'}
                                                </span>
                                                {isSpeaking && <div className={styles.soundWaves}>
                                                    <div className={styles.wave}></div>
                                                    <div className={styles.wave}></div>
                                                    <div className={styles.wave}></div>
                                                </div>}
                                            </button>

                                            <button
                                                className={`${styles.actionButton} ${styles.secondary}`}
                                                onClick={handleCopy}
                                                disabled={copySuccess}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/>
                                                </svg>
                                                {copySuccess ? 'Copied!' : 'Copy'}
                                            </button>

                                            <button className={`${styles.actionButton} ${styles.secondary}`} onClick={handleDownload}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2"/>
                                                </svg>
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === TabType.CHAT && loadingState === LoadingState.SUCCESS && summary && (
                            <div className={styles.chatContainer}>
                                <div className={styles.chatMessages}>
                                    {chatMessages.map((message) => (
                                        <div key={message.id} className={`${styles.message} ${styles[message.type]}`}>
                                            <div className={styles.messageContent}>
                                                <FormattedMessageContent content={message.content} />
                                            </div>
                                        </div>
                                    ))}
                                    {chatLoading && (
                                        <div className={`${styles.message} ${styles.assistant} ${styles.loading}`}>
                                            <div className={styles.messageContent}>
                                                <div className={styles.typingIndicator}>
                                                    <div className={styles.dot}></div>
                                                    <div className={styles.dot}></div>
                                                    <div className={styles.dot}></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div ref={chatEndRef} />
                                </div>

                                <div className={styles.chatInputSection}>
                                    <div className={styles.defaultQuestions}>
                                        {DEFAULT_CHAT_QUESTIONS.map((question, index) => (
                                            <button
                                                key={index}
                                                className={styles.quickQuestion}
                                                onClick={() => handleSuggestedQuestion(question)}
                                                disabled={chatLoading}
                                            >
                                                {question}
                                            </button>
                                        ))}
                                    </div>

                                    <form className={styles.chatForm} onSubmit={handleSubmitQuestion}>
                                        <div className={styles.inputContainer}>
                                            <input
                                                type="text"
                                                value={currentQuestion}
                                                onChange={(e) => setCurrentQuestion(e.target.value)}
                                                placeholder="Ask a question about this engagement letter..."
                                                className={styles.chatInput}
                                                disabled={chatLoading}
                                            />
                                            <button
                                                type="submit"
                                                className={styles.submitButton}
                                                disabled={!currentQuestion.trim() || chatLoading}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummaryModal;